import { isValidElement, type PropsWithChildren, type ReactNode, useState } from 'react';
import IconInfo from '~/vendor/cxy/icons/icons/Info';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import clsx from 'clsx';

import type { ButtonProps } from '../Button';
import { Button } from '../Button';

import styles from './styles/Tooltip.module.scss';

const Tooltip = ({ children, onOpenChange, open, ...props }: RadixTooltip.TooltipProps) => {
  return (
    <RadixTooltip.Provider>
      <RadixTooltip.Root onOpenChange={onOpenChange} open={open} {...props}>
        {children}
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
};

type BaseTriggerProps = {
  'aria-label'?: string;
  asChild?: boolean;
  className?: string;
  onClick?: () => void;
};

type TooltipTriggerProps = Omit<ButtonProps, keyof BaseTriggerProps> &
  BaseTriggerProps & {
    children?: ReactNode;
  };

const TooltipTrigger = ({
  'aria-label': ariaLabel = 'tooltip',
  asChild,
  children,
  className,
  onClick,
  ...restProps
}: TooltipTriggerProps) => {
  const renderTriggerContent = () => {
    if (asChild) {
      if (!isValidElement(children)) {
        console.warn('Tooltip.Trigger: asChild prop requires a single valid element as children');
        return null;
      }
      return children;
    }

    return (
      <Button className={clsx(styles.button, className)} variant="ghost" {...restProps}>
        {children || <IconInfo opacity={0.6} />}
      </Button>
    );
  };

  return (
    <RadixTooltip.Trigger aria-label={ariaLabel} asChild className={className} onClick={onClick}>
      {renderTriggerContent()}
    </RadixTooltip.Trigger>
  );
};

const TooltipContent = ({
  children,
  className,
  ...props
}: {
  className?: string;
} & RadixTooltip.TooltipContentProps) => {
  return (
    <RadixTooltip.Portal>
      <RadixTooltip.Content
        className={clsx(styles.content, className)}
        data-cxy-name="tooltip"
        sideOffset={5}
        {...props}
      >
        {children}
        <RadixTooltip.Arrow className={styles.arrow} />
      </RadixTooltip.Content>
    </RadixTooltip.Portal>
  );
};

Tooltip.Trigger = TooltipTrigger;
Tooltip.Content = TooltipContent;

const TooltipInfo = ({ children, side }: Pick<RadixTooltip.TooltipContentProps, 'side'> & PropsWithChildren) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Tooltip onOpenChange={setIsOpen} open={isOpen}>
      <Tooltip.Trigger onClick={() => setIsOpen(!isOpen)} />
      <Tooltip.Content side={side}>{children}</Tooltip.Content>
    </Tooltip>
  );
};

export { Tooltip, TooltipInfo };
